import { CargoPortProps } from '@api/models/cargo-port';
import {
	isDischargePort,
	isLoadPort,
} from '../cargoPortHelpers';
import {
	LaytimeCalculationMethods,
	LaytimeDaysPerWeek,
	LaytimeIntervals,
	LaytimeTerms,
} from '../constants';
import { round } from '../math';
import normalize from '../normalizeNumber';

type CargoTimeAllowed = {
	timeAllowed?: number | null;
	timeAllowedInterval?: LaytimeIntervals;
	timeAllowedLoading?: number | null;
	timeAllowedDischarging?: number | null;
	laytimeTerms: LaytimeTerms | null;
	CargoPorts: Array<CargoPortProps>;
};

export const calculateTimeAllowed = ({
	quantity,
	rate,
	method,
	prorate = false,
	cargo,
	cargoPort,
}: {
	quantity: number | null;
	rate: number | null;
	method: LaytimeCalculationMethods | null;
	prorate?: boolean;
	cargo?: CargoTimeAllowed;
	cargoPort?: CargoPortProps;
}) => {
	const timeToUse = cargo?.timeAllowed;

	// If cargo has timeAllowed defined, use that instead of calculating from quantity/rate
	if (timeToUse && cargo && cargo.laytimeTerms === LaytimeTerms.REVERSIBLE) {
		const { laytimeTerms } = cargo;
		const timeAllowed = normalize(timeToUse);
		const factor = method ? LaytimeDaysPerWeek[method] ?? 7 : 7;

		let timeAllowedInDays = timeAllowed;

		if (cargo.timeAllowedInterval === LaytimeIntervals.HOUR) {
			timeAllowedInDays = timeAllowed / 24;
		}

		if (laytimeTerms === LaytimeTerms.REVERSIBLE) {
			timeAllowedInDays /= cargo.CargoPorts.length;
		}

		if (prorate) {
			return normalize(round(timeAllowedInDays * (7 / factor), 6));
		}

		return normalize(round(timeAllowedInDays, 6));
	}

	if (cargo?.laytimeTerms === LaytimeTerms.AVERAGED && cargoPort) {
		const isLoad = isLoadPort(cargoPort);

		if (isLoad && cargo?.CargoPorts) {
			const timeAllowed = normalize(cargo.timeAllowedLoading);
			let timeAllowedInDays = timeAllowed;
			const loadPorts = cargo.CargoPorts.filter(isLoadPort);

			if (cargo.timeAllowedInterval === LaytimeIntervals.HOUR) {
				timeAllowedInDays = timeAllowed / 24;
			}

			return normalize(round(timeAllowedInDays / loadPorts.length, 6));
		}

		const timeAllowed = normalize(cargo.timeAllowedDischarging);
		let timeAllowedInDays = timeAllowed;
		const disPorts = cargo.CargoPorts.filter(isDischargePort);

		if (cargo.timeAllowedInterval === LaytimeIntervals.HOUR) {
			timeAllowedInDays = timeAllowed / 24;
		}

		return normalize(round(timeAllowedInDays / disPorts.length, 6));
	}

	const roundNorm = (value: number) => normalize(round(value, 6));

	const factor = method != null ? (LaytimeDaysPerWeek[method] ?? 7) : 7;
	const prorateFactor = prorate ? 7 / factor : 1;

	if (
		cargoPort?.timeAllowedOverride != null
	) {
		const base = cargoPort.timeAllowedOverride;

		return roundNorm(base * prorateFactor);
	}

	if (quantity == null || rate == null) {
		return null;
	}

	const base = normalize(quantity) / normalize(rate);

	return roundNorm(base * prorateFactor);
};
